// src/promo/PromoPill.jsx
//
// Bottom-LEFT promo for the DYNAMIC upgrade ladder. What it offers is
// decided entirely by resolveOffer(user) — Free→Super, Super→Creator, …,
// Enterprise→Coins — so the label, price and modal copy all change with
// the user's tier. THREE resting forms, never fully dismissable:
//
//   • full pill  "Get 55% off <Tier>"  (wide screens, first impression)
//   • badge      "%"                    (minimized — the only "close" target)
//   • modal      email capture          (opens on tap of either)
//
// VISIBILITY (this file) vs WHAT (offer.js): we hide the offer while a
// promo discount is already active on the user's sub (they just claimed →
// it reappears next cycle for the next rung) and for unhealthy billing
// states. The wide pill sits above the dating rail (z 14200); narrow
// screens default to the badge so it never crowds that rail.
//
// Tap → POST /api/leads → /profile/wallet?autobuy=<tier>&cycle=monthly&promo=<id>
// (logged-out users go via /login?next= so the discount survives sign-in).

import React, { useContext, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../user/AuthContext";
import { auth } from "../firebase";
import { resolveOffer, hasActivePromoDiscount } from "./offer";
import { startMembershipCheckout } from "./membershipCheckout";
import "./PromoPill.css";

const BADGE_GLYPH = "%";
const CYCLE = "monthly";
const API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
// Persisted "minimized to badge" preference (campaign-agnostic — it's a
// UI rest state, NOT a dismissal; the offer never goes away).
const MIN_KEY = "promo:minimized";
// Below this width the wide pill would crowd the bottom rail, so we rest
// as the badge by default.
const NARROW_PX = 768;

// Routes where the pill would be intrusive or redundant.
const HIDE_PREFIXES = [
  "/login",
  "/signup",
  "/forgot-password",
  "/success",
  "/cancel",
  "/profile", // wallet / billing / account — already in the funnel
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function initialMinimized() {
  try {
    const v = localStorage.getItem(MIN_KEY);
    if (v === "1") return true;
    if (v === "0") return false;
  } catch {}
  // No stored preference → minimize by default on narrow screens.
  if (typeof window !== "undefined" && window.innerWidth <= NARROW_PX) return true;
  return false;
}

function rememberMinimized(min) {
  try {
    localStorage.setItem(MIN_KEY, min ? "1" : "0");
  } catch {}
}

export default function PromoPill() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, authLoading } = useContext(AuthContext);

  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(initialMinimized);
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | submitting | error
  const [error, setError] = useState("");

  // WHAT to offer this user (next rung up, or coins at the top).
  const offer = useMemo(() => resolveOffer(user), [user]);

  // Eligibility (WHETHER to show). We DO upsell existing members now — but
  // only healthy ones, and never while a promo discount is already active
  // this cycle (they just claimed → reappears next cycle for the next rung).
  const eligible = useMemo(() => {
    if (hasActivePromoDiscount(user)) return false;
    if (offer.kind === "coins") return true; // top of ladder, always offer coins
    const vip = user?.subscriptions?.vipPass;
    if (!vip) return true; // free / no sub → offer the first paid tier
    const s = String(vip.status || "").toLowerCase();
    const healthy =
      (s === "active" || s === "trialing") &&
      !(vip.cancel_at_period_end === true || vip.cancelAtPeriodEnd === true);
    return healthy; // hide for past_due / incomplete / canceled / leaving
  }, [user, offer]);

  // Logged-in users with an account email: lock the field to it so they
  // can't (accidentally or maliciously) subscribe another address.
  const accountEmail = String(user?.email || "").trim().toLowerCase();
  const lockedEmail = !!user && !!accountEmail;

  const onHiddenRoute = useMemo(
    () => HIDE_PREFIXES.some((p) => location.pathname.startsWith(p)),
    [location.pathname]
  );

  // Gate: wait for auth so the wrong offer never flashes.
  if (authLoading || onHiddenRoute || !eligible) return null;

  const minimize = () => {
    setOpen(false);
    setMinimized(true);
    rememberMinimized(true);
  };

  const openModal = () => setOpen(true);

  const goToCheckout = () => {
    // Remember the page we're leaving so Stripe "cancel"/back returns HERE,
    // not the wallet. sessionStorage, not the URL (no PII in querystrings).
    try {
      sessionStorage.setItem("promo:returnTo", location.pathname + location.search);
    } catch {}

    let dest;
    if (offer.kind === "coins") {
      dest = offer.href; // coins page runs its own discounted top-up
    } else {
      const qs = new URLSearchParams({
        autobuy: offer.targetKey,
        cycle: CYCLE,
        promo: offer.promoId,
      }).toString();
      dest = `/profile/wallet?${qs}`;
    }
    // Both destinations live under PrivateRoute — route logged-out users
    // through /login?next= so the offer survives sign-in.
    if (user) navigate(dest);
    else navigate(`/login?next=${encodeURIComponent(dest)}`);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    // Logged-in users with an account email submit that (field is locked);
    // everyone else submits what they typed.
    const value = lockedEmail ? accountEmail : email.trim();
    if (!EMAIL_RE.test(value)) {
      setError("Enter a valid email");
      setStatus("error");
      return;
    }
    setStatus("submitting");
    setError("");

    // Remember the page we're leaving so Stripe "cancel"/back returns HERE,
    // not the wallet. sessionStorage, not the URL (no PII in querystrings).
    const returnTo = location.pathname + location.search;
    try {
      sessionStorage.setItem("promo:returnTo", returnTo);
    } catch {}

    // Phone-only / logged-out: stash the email so checkout can prefill
    // Stripe with it when the account has none. sessionStorage, not URL.
    try {
      if (!accountEmail) sessionStorage.setItem("promo:pendingEmail", value);
    } catch {}

    // ID token once (logged-in only) — reused to bind the lead AND to start
    // the direct Stripe checkout below.
    let token = "";
    try {
      token = (await auth.currentUser?.getIdToken?.()) || "";
    } catch {}

    // Best-effort lead capture — never block the sale on a save hiccup.
    // The token lets the backend bind the lead to the account and mark
    // consent "confirmed" for the account email.
    try {
      await fetch(`${API_BASE}/api/leads`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          email: value,
          source: "promo_pill",
          promoId: offer.promoId,
        }),
      });
    } catch {
      /* swallow — we still want the user to reach checkout */
    }
    // Rest as the badge when they come back from checkout.
    rememberMinimized(true);

    const isFirstPaidTier = offer.kind === "membership" && offer.mode === "new";

    // A LOGGED-IN user claiming their first paid tier goes STRAIGHT to Stripe
    // from the modal — skipping the wallet ?autobuy countdown. Success lands
    // on the wallet (shows the now-active membership); cancel returns HERE.
    if (!!user && !!token && isFirstPaidTier) {
      try {
        await startMembershipCheckout({
          planKey: offer.targetKey,
          cycle: CYCLE,
          promoId: offer.promoId,
          returnTo,
          successReturnTo: "/profile/wallet",
          idToken: token,
        });
        return; // redirecting to Stripe — nothing left to do here
      } catch {
        /* fall through to the wallet handoff below */
      }
    }

    // A LOGGED-OUT user claiming their first paid tier registers first: carry
    // the typed email + checkout intent to /signup, which (on email signup)
    // sends them straight to Stripe afterwards — "sign up doubles as the
    // start of checkout". The wallet ?autobuy URL rides along as `next` so a
    // phone-OTP signup (or any fallback) still resumes the discounted buy.
    if (!user && isFirstPaidTier) {
      const walletNext = `/profile/wallet?autobuy=${offer.targetKey}&cycle=${CYCLE}&promo=${offer.promoId}`;
      try {
        sessionStorage.setItem(
          "promo:signupCheckout",
          JSON.stringify({
            planKey: offer.targetKey,
            cycle: CYCLE,
            promoId: offer.promoId,
            returnTo,
            email: value,
          })
        );
      } catch {}
      navigate(`/signup?next=${encodeURIComponent(walletNext)}`);
      return;
    }

    // Everyone else (existing-member upgrade, coins, or any fallback) uses
    // the wallet/login handoff.
    goToCheckout();
  };

  return (
    <div className="promo-anchor">
      {open && (
        <form className="promo-card" onSubmit={submit}>
          <button
            type="button"
            className="promo-card__close"
            aria-label="Minimize"
            title="Minimize"
            onClick={minimize}
          >
            ×
          </button>

          <div className="promo-card__headline">{offer.headline}</div>
          <div className="promo-card__subhead">{offer.subhead}</div>

          {lockedEmail ? (
            // We already know this signed-in user's email — show it locked
            // so they can't subscribe another address. Claiming for an
            // account = consent we can prove.
            <div
              className="promo-card__locked"
              title={accountEmail}
              aria-label={`Claiming for ${accountEmail}`}
            >
              {accountEmail}
            </div>
          ) : (
            <input
              className="promo-card__input"
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="you@email.com"
              value={email}
              onChange={(ev) => {
                setEmail(ev.target.value);
                if (status === "error") {
                  setStatus("idle");
                  setError("");
                }
              }}
              autoFocus
            />
          )}

          <button
            type="submit"
            className="promo-card__cta"
            disabled={status === "submitting"}
          >
            {status === "submitting" ? "One sec…" : `Claim ${offer.percentOff}% off →`}
          </button>

          {error ? (
            <div className="promo-card__error">{error}</div>
          ) : (
            <div className="promo-card__fine">{offer.fineprint}</div>
          )}
        </form>
      )}

      {minimized ? (
        // Minimized badge — the persistent, never-removed state.
        <button
          type="button"
          className={`promo-badge${open ? " is-open" : ""}`}
          onClick={openModal}
          aria-label={`${offer.pillLabel} — open`}
          title={offer.pillLabel}
        >
          {BADGE_GLYPH}
        </button>
      ) : (
        // Full pill. The body opens the modal; the trailing × minimizes to
        // the badge (it does NOT remove the offer). Container is a div so
        // we don't nest a <button> inside a <button>.
        <div className={`promo-pill${open ? " is-open" : ""}`}>
          <button
            type="button"
            className="promo-pill__main"
            onClick={openModal}
            aria-expanded={open}
          >
            <span className="promo-pill__spark" aria-hidden="true">
              {BADGE_GLYPH}
            </span>
            <span className="promo-pill__label">{offer.pillLabel}</span>
          </button>
          <button
            type="button"
            className="promo-pill__min"
            onClick={minimize}
            aria-label="Minimize"
            title="Minimize"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}
